/**
 * PHA-1033 / PHA-1046: build the Review-step list for a care-plan REFRESH.
 *
 * Requirement 2.1 — "By default select all problems and medications that were there
 * before. Mark V for those selected before, X those rejected before and NEW for those new,
 * compared to last approved care plan."
 *
 * Why this is a separate module rather than an extension of `compareItems`:
 * `compareItems` answers a different question (new-candidate review during onboarding) and
 * has exactly two flags — `isNew` and `isModified`. It has no notion of "the clinician
 * rejected this last time", because the onboarding flow it serves has no 'last time'. The
 * refresh flow does, and that third state is the whole point of the requirement.
 *
 * The three inputs come from three different places, and mixing them up is the bug this
 * module exists to prevent:
 *   - `approved`       — `patient.problems` / `patient.medications`. APPROVED-ONLY. A
 *                        rejected item is simply absent here; it is not present-and-false.
 *   - `priorCandidates`— the most recent candidate batch (`patientApi.getCandidates`).
 *                        This is the ONLY place a rejection is recorded, as
 *                        `approved=false, reviewed=true`.
 *   - `sourced`        — whatever the chosen refresh source produced (the chart's current
 *                        lists, a re-parse of the stored images, a fresh EHR pull, or a
 *                        new upload).
 */

/** Metadata the UI attaches; never part of a clinical comparison or an approve payload. */
export const REFRESH_DIFF_METADATA_FIELDS = [
  'uploaded_ts',
  'uploaded_by',
  'attributed_ts',
  'isNew',
  'isModified',
  'originalValue',
  'approved',
  'icd10_needs_review',
  'priorState',
];

export type PriorState = 'kept' | 'rejected' | 'new';

/**
 * The sourced item, plus:
 *  - `priorState` — how it stood on the last approved care plan (the V / X / NEW marker)
 *  - `approved`   — the pre-checked state, derived from `priorState` and NEVER from
 *                   `isModified` (that coupling was PHA-725; PHA-1029 fixed re-deriving it).
 *                   `undefined` = undecided, only ever for a NEW item under
 *                   `newItemsUndecided` (PHA-1141).
 */
export type RefreshReviewItem<T = any> = T & {
  priorState: PriorState;
  approved: boolean | undefined;
  isModified?: boolean;
  originalValue?: any;
};

const normalize = (item: any) => {
  const copy = { ...item };
  REFRESH_DIFF_METADATA_FIELDS.forEach((f) => delete copy[f]);
  return copy;
};

const keyOf = (item: any, keyField: string): string =>
  String(item?.[keyField] ?? '').toLowerCase().trim();

export interface RefreshDiffInput<T> {
  /** Items on the last APPROVED care plan. */
  approved: T[];
  /**
   * Most recent candidate batch, carrying the clinician's stored approve/reject decisions.
   * Deliberately untyped: these are raw candidate rows, a different shape from the sourced
   * items — only the key field and `approved` are read off them.
   */
  priorCandidates: any[];
  /** Items produced by the chosen refresh source. */
  sourced: T[];
  /** 'problem' or 'medication' — the field items are identified by. */
  keyField: string;
  /**
   * PHA-1141: leave NEW items undecided instead of pre-checking them. Set for the refreshes
   * that review what the EHR sent ('ehr', 'ehr_changes'): "highlighting any changes needed to
   * be reviewed with 'new' label (not accepted automatically)". The clinician must accept or
   * reject each one before continuing (ApprovalWorkflow blocks until they do).
   */
  newItemsUndecided?: boolean;
}

/** PHA-1141: a NEW item the clinician has not yet accepted or rejected. */
export const isUndecidedNewItem = (item: any): boolean =>
  item?.priorState === 'new' && item?.approved !== true && item?.approved !== false;

/**
 * Union of (everything the new source produced) and (everything rejected last time), each
 * stamped with its prior state and a default selection.
 *
 * Deliberate asymmetry: an item that was KEPT last time but is absent from the new source
 * is NOT resurrected — a fresh EHR pull or a new upload that no longer lists a problem is
 * saying the problem is resolved, and the new source is authoritative. A REJECTED item is
 * always carried through, so the clinician can see the prior rejection and reverse it in
 * place (confirmed decision on PHA-1046, 2026-08-20).
 */
export function buildRefreshReviewList<T extends Record<string, any>>({
  approved,
  priorCandidates,
  sourced,
  keyField,
  newItemsUndecided = false,
}: RefreshDiffInput<T>): RefreshReviewItem<T>[] {
  const approvedByKey = new Map<string, any>();
  for (const item of approved || []) {
    const key = keyOf(item, keyField);
    if (key) approvedByKey.set(key, item);
  }

  // A rejection is only meaningful on a REVIEWED batch: an unreviewed batch's
  // `approved=false` is the EHR import's default, not a clinician decision (PHA-725 writes
  // it, PHA-1029 is what happens when you read it as one).
  const rejectedByKey = new Map<string, any>();
  for (const item of priorCandidates || []) {
    const key = keyOf(item, keyField);
    if (!key || item?.approved !== false || approvedByKey.has(key)) continue;
    rejectedByKey.set(key, item);
  }

  const out: RefreshReviewItem<T>[] = [];
  const seen = new Set<string>();

  for (const item of sourced || []) {
    const key = keyOf(item, keyField);
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const priorApproved = approvedByKey.get(key);
    const priorState: PriorState = priorApproved
      ? 'kept'
      : rejectedByKey.has(key)
        ? 'rejected'
        : 'new';

    const isModified =
      priorApproved !== undefined &&
      JSON.stringify(normalize(item)) !== JSON.stringify(normalize(priorApproved));

    out.push({
      ...item,
      priorState,
      isModified,
      originalValue: isModified ? priorApproved : undefined,
      approved:
        priorState === 'rejected' ? false : priorState === 'new' && newItemsUndecided ? undefined : true,
    });
  }

  // Rejections the new source no longer lists still have to be visible as X, or the
  // clinician has no way to reverse a rejection they now disagree with.
  for (const [key, item] of rejectedByKey) {
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...item, priorState: 'rejected', approved: false });
  }

  return out;
}

/** The V / X / NEW chip text for an item. */
export function getPriorStateLabel(priorState: PriorState | undefined): string | null {
  switch (priorState) {
    case 'kept':
      return 'On current plan';
    case 'rejected':
      return 'Previously rejected';
    case 'new':
      return 'New';
    default:
      return null;
  }
}
